import type { ProductStatus, SmartCreationDraft } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { ProductThumbnail } from "@/components/shared/product-thumbnail";
import { useProductPreviewStatuses } from "@/hooks/use-product-preview-statuses";
import { useIsVendorShop } from "@/hooks/use-shop-account-type";
import { cn } from "@/lib/utils";

export function SelectedProductsWithStatus({
  draft,
  shopId,
}: {
  draft: SmartCreationDraft;
  shopId: string;
}) {
  const productStatuses = useProductPreviewStatuses(shopId, draft.products.slice(0, 4));
  const vendor = useIsVendorShop(shopId);
  return (
    <SelectedProductsContext draft={draft} productStatuses={productStatuses} vendor={vendor} />
  );
}

export function SelectedProductsContext({
  draft,
  productStatuses,
  vendor = false,
}: {
  draft: SmartCreationDraft;
  productStatuses?: ReadonlyMap<string, ProductStatus>;
  /** A vendor's products are keyed by ASIN and have no SKU to show (#674). */
  vendor?: boolean;
}) {
  const { t } = useTranslation("smartCreation");
  const first = draft.products[0];
  return (
    <section className="flex items-center gap-3 rounded-md border border-line bg-surface p-2.5">
      <div className="group/products flex shrink-0 items-center">
        {draft.products.slice(0, 4).map((product, index) => (
          <div
            key={product.sku}
            className={cn(
              "rounded-md transition-[margin] duration-200 ease-out",
              index > 0 && "-ml-4 group-hover/products:ml-1",
            )}
          >
            <ProductThumbnail
              imageUrl={product.imageUrl}
              label={product.title ?? product.asin}
              asin={product.asin}
              sku={vendor ? undefined : product.sku}
              productStatus={productStatuses?.get(product.sku)}
              size="context"
            />
          </div>
        ))}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">
          {t("objectives.products.selected", { count: draft.products.length })}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {first
            ? t("objectives.products.summary", {
                title: first.title ?? first.asin,
                asin: first.asin,
              })
            : "—"}
        </p>
      </div>
    </section>
  );
}
